import { action, computed, makeObservable, observable } from 'mobx';
import type { ObservableMap } from 'mobx';

import { isObjectEmpty } from '../../../shared/utils';
import type { TManualAnomalyRowData } from '../types';

import type { ManualAnomaly } from './ManualAnomaly.entity';

type TManualAnomalyRowDataFields = keyof TManualAnomalyRowData;
const staticRequiredFields: TManualAnomalyRowDataFields[] = ['indexFormat', 'riskLevel'];

type TErrorsObject = Partial<Record<TManualAnomalyRowDataFields, string[]>>;

type TViolation = {
  attrName: string;
  message: string;
  targetType: 'INDEX';
  target: number;
};
type TRequestValidationError = Error & {
  violations: TViolation[];
};

export class ManualAnomaliesValidationService {
  readonly _errors = observable.map<string, TErrorsObject>();
  readonly requestErrors = observable.map<number, TErrorsObject>();

  constructor() {
    makeObservable(this);
  }

  static isRequestValidationError(e: unknown): e is TRequestValidationError {
    return typeof e === 'object' && e != null && 'violations' in e;
  }

  private addError(field: TManualAnomalyRowDataFields, item: ManualAnomaly, errorKey: string): void {
    const errorObject = this._errors.get(item.entityId) ?? {};
    const errorsSet = new Set(errorObject[field] ?? []);

    errorsSet.add(errorKey);

    this._errors.set(item.entityId, {
      ...errorObject,
      [field]: Array.from(errorsSet),
    });
  }

  private removeError(field: TManualAnomalyRowDataFields, item: ManualAnomaly, errorKey: string): void {
    const errorObject = this._errors.get(item.entityId) ?? {};
    const errorsSet = new Set(errorObject[field] ?? []);

    errorsSet.delete(errorKey);

    this._errors.set(item.entityId, {
      ...errorObject,
      [field]: Array.from(errorsSet),
    });
  }

  private removeErrors(field: TManualAnomalyRowDataFields, item: ManualAnomaly): void {
    const errorObject = this._errors.get(item.entityId) ?? {};
    const errorsSet = new Set(errorObject[field] ?? []);

    errorsSet.clear();

    this._errors.set(item.entityId, {
      ...errorObject,
      [field]: Array.from(errorsSet),
    });
  }

  @computed
  get errors(): ObservableMap<string, TErrorsObject> {
    return this._errors;
  }

  @action.bound
  processRequestValidationError(violations: TViolation[], sequence: ManualAnomaly[]): void {
    violations.forEach((violation) => {
      switch (violation.targetType) {
        case 'INDEX': {
          const targetSequence = sequence[violation.target];

          if (!targetSequence) {
            console.error(`cannot find corresponding sequence by index ${violation.target}`);
            return;
          }

          this.addError(violation.attrName as TManualAnomalyRowDataFields, targetSequence, violation.message);
        }
      }
    });
  }

  private checkFieldRequired(field: TManualAnomalyRowDataFields, item: ManualAnomaly): void {
    const errorObject = this._errors.get(item.entityId);
    const requiredByFormat = this.getRequiredFieldsByFormat(item);

    if (item.data[field] != null) {
      if (!errorObject || isObjectEmpty(errorObject)) {
        return;
      }

      this.removeError(field, item, 'alertsManagerWidget:errorMessages.requiredField');

      return;
    }

    if (staticRequiredFields.includes(field) || requiredByFormat.includes(field)) {
      this.addError(field, item, 'alertsManagerWidget:errorMessages.requiredField');
    }
  }

  validateManualAnomalyField(field: TManualAnomalyRowDataFields, item: ManualAnomaly): boolean {
    this.removeErrors(field, item);

    this.checkFieldRequired(field, item);

    return !!this._errors.get(item.entityId)?.[field]?.length;
  }

  validateManualAnomalyItem(item: ManualAnomaly): boolean {
    let hasErrors = false;

    const fieldsToValidate = [...new Set([...staticRequiredFields, ...this.getRequiredFieldsByFormat(item)])];

    for (const field of fieldsToValidate) {
      const hasFieldErrors = this.validateManualAnomalyField(field, item);

      if (hasFieldErrors && !hasErrors) {
        hasErrors = hasFieldErrors;
      }
    }

    return hasErrors;
  }

  getRequiredFieldsByFormat(item: ManualAnomaly): TManualAnomalyRowDataFields[] {
    const indexFormat = item.data.indexFormat;

    if (indexFormat === 'LOG_TIME') {
      return ['anomalyAt'];
    }

    if (indexFormat === 'LOG_DEPTH') {
      return ['wellboreId'];
    }

    return [];
  }

  validateAnomalies(anomalies: ManualAnomaly[]): boolean {
    let hasErrors = false;

    for (const anomaly of anomalies) {
      const hasItemErrors = this.validateManualAnomalyItem(anomaly);

      if (hasItemErrors && !hasErrors) {
        hasErrors = hasItemErrors;
      }
    }

    return hasErrors;
  }
}
